import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, PlusCircle, Search, Bus, Archive, RotateCcw, Trash2, AlertTriangle } from "lucide-react";

export default function Projects() {
  const { user, can } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [projects, setProjects] = useState(null);
  const [archived, setArchived] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [purgeQueue, setPurgeQueue] = useState([]);
  const [permTarget, setPermTarget] = useState(null);

  const canDelete = can("projects.delete");

  const loadArchived = useCallback(() => {
    if (!canDelete) return;
    api.get("/projects/archived").then((r) => {
      setArchived(r.data);
      setPurgeQueue(r.data.filter((p) => p.purge_due));
    }).catch(() => {});
  }, [canDelete]);

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data));
    loadArchived();
  }, [loadArchived]);

  if (!projects)
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>;

  const filtered = projects.filter(
    (p) =>
      (p.name || "").toLowerCase().includes(q.toLowerCase()) ||
      p.id.toLowerCase().includes(q.toLowerCase()) ||
      (p.company_name || "").toLowerCase().includes(q.toLowerCase())
  );

  const restore = async (p) => {
    setBusy(true);
    try { await api.post(`/projects/${p.id}/restore`); toast.success(t("projects.restored")); api.get("/projects").then((r) => setProjects(r.data)); loadArchived(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const permanentDelete = async (p) => {
    setBusy(true);
    try {
      await api.delete(`/projects/${p.id}/permanent`);
      toast.success(t("projects.permanentlyDeleted"));
      setPermTarget(null);
      setPurgeQueue((qq) => qq.filter((x) => x.id !== p.id));
      loadArchived();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const currentPurge = purgeQueue[0];

  return (
    <div className="space-y-6" data-testid="projects-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-black text-slate-900">
            {user.role === "technician" ? t("projects.myTasks") : user.role === "customer" ? t("projects.fulfillment") : t("projects.title")}
          </h1>
          <p className="mt-1 text-slate-500">{t("projects.count", { count: filtered.length })}</p>
        </div>
        {["project_manager", "super_admin"].includes(user.role) && (
          <Button data-testid="new-project-btn" onClick={() => navigate("/projects/new")} className="gap-2 bg-blue-600 hover:bg-blue-500">
            <PlusCircle className="h-4 w-4" /> {t("projects.newProject")}
          </Button>
        )}
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input data-testid="project-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("projects.searchPlaceholder")} className="h-11 border-slate-300 bg-white pl-9" />
      </div>

      <div className="grid gap-3">
        {filtered.length === 0 && <p className="text-sm text-slate-400">{t("projects.none")}</p>}
        {filtered.map((p) => (
          <Card
            key={p.id}
            data-testid={`project-row-${p.id}`}
            onClick={() => navigate(`/projects/${p.id}`)}
            className="flex cursor-pointer flex-wrap items-center justify-between gap-3 border-slate-200 bg-white p-4 shadow-sm transition-colors hover:border-blue-400"
          >
            <div className="flex items-center gap-4">
              <div className="flex h-11 w-11 items-center justify-center rounded-md bg-blue-100 text-blue-600">
                <Bus className="h-5 w-5" />
              </div>
              <div>
                <div className="font-heading font-bold text-slate-900">{p.name || p.id}</div>
                <div className="text-sm text-slate-500">{p.id} · {p.company_name}</div>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-right">
                <div className="font-heading text-lg font-bold text-slate-900">{p.total_buses}</div>
                <div className="overline text-slate-500">{t("projects.buses")}</div>
              </div>
              <div className="text-right">
                <div className="font-heading text-lg font-bold text-slate-900">{p.parts_shipped}</div>
                <div className="overline text-slate-500">{t("projects.parts")}</div>
              </div>
              <StatusBadge status={p.status} />
            </div>
          </Card>
        ))}
      </div>

      {canDelete && archived.length > 0 && (
        <div className="pt-4" data-testid="archived-section">
          <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-bold text-slate-700">
            <Archive className="h-5 w-5 text-slate-400" /> {t("projects.archivedTitle")}
            <span className="text-sm font-normal text-slate-400">· {t("projects.archivedCount", { count: archived.length })}</span>
          </h2>
          <div className="grid gap-3">
            {archived.map((p) => (
              <Card key={p.id} data-testid={`archived-row-${p.id}`} className="flex flex-wrap items-center justify-between gap-3 border-slate-200 bg-slate-50 p-4">
                <div className="flex items-center gap-4">
                  <div className="flex h-11 w-11 items-center justify-center rounded-md bg-slate-200 text-slate-500"><Archive className="h-5 w-5" /></div>
                  <div>
                    <div className="font-heading font-bold text-slate-700">{p.name || p.id}</div>
                    <div className="text-sm text-slate-500">{p.id} · {p.company_name}</div>
                    <div className={`text-xs ${p.purge_due ? "font-semibold text-red-600" : "text-slate-400"}`}>{t("projects.daysArchived", { days: p.days_in_archive })}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" data-testid={`restore-${p.id}`} onClick={() => restore(p)} disabled={busy} className="gap-1.5 border-slate-300"><RotateCcw className="h-4 w-4" /> {t("projects.restore")}</Button>
                  <Button size="sm" variant="outline" data-testid={`perm-delete-${p.id}`} onClick={() => setPermTarget(p)} disabled={busy} className="gap-1.5 border-red-300 text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" /> {t("projects.deletePermanently")}</Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* 180-day purge prompt */}
      <Dialog open={!!currentPurge} onOpenChange={(o) => { if (!o) setPurgeQueue((qq) => qq.slice(1)); }}>
        <DialogContent className="border-slate-200 bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading text-slate-900"><AlertTriangle className="h-5 w-5 text-amber-500" /> {t("projects.purgeTitle")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            <span className="font-semibold text-slate-900">{currentPurge?.name || currentPurge?.id}</span> — {t("projects.purgeMessage", { days: currentPurge?.days_in_archive })}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPurgeQueue((qq) => qq.slice(1))} className="border-slate-300">{t("projects.keep")}</Button>
            <Button data-testid="purge-delete-btn" onClick={() => { permanentDelete(currentPurge); setPurgeQueue((qq) => qq.slice(1)); }} disabled={busy} className="gap-1.5 bg-red-600 hover:bg-red-500">
              <Trash2 className="h-4 w-4" /> {t("projects.deletePermanently")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* manual permanent delete confirm */}
      <Dialog open={!!permTarget} onOpenChange={() => setPermTarget(null)}>
        <DialogContent className="border-slate-200 bg-white">
          <DialogHeader><DialogTitle className="font-heading text-slate-900">{t("projects.deletePermanently")}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">{t("projects.confirmPermanent", { name: permTarget?.name || permTarget?.id })}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPermTarget(null)} className="border-slate-300">{t("projects.cancel")}</Button>
            <Button data-testid="confirm-perm-delete" onClick={() => permanentDelete(permTarget)} disabled={busy} className="gap-1.5 bg-red-600 hover:bg-red-500">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} {t("projects.deletePermanently")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
